/**
 * Capacity Management page — /admin/capacity
 *
 * Org admins choose which EC2 instance types their users may launch and set
 * the total EBS storage quota for the org.
 */

import { useEffect, useMemo, useState } from "react";
import {
  Card,
  Checkbox,
  Button,
  Tag,
  Tooltip,
  Typography,
  Space,
  Alert,
  Spin,
  message,
  Divider,
  InputNumber
} from "antd";
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  SaveOutlined,
  InfoCircleOutlined,
  DatabaseOutlined
} from "@ant-design/icons";
import { useSelector } from "react-redux";
import axiosInstance from "../../utils/axios";
import { trackJourney } from "../../lib/journeyAnalytics";

const { Title, Text } = Typography;

function CapacityManagement() {
  const { user } = useSelector((state) => state.auth);
  const [instanceTypes, setInstanceTypes] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // EBS storage state
  const [storageQuota, setStorageQuota] = useState(null);   // current quota from API
  const [storageInput, setStorageInput] = useState(null);    // edited value
  const [storageLoading, setStorageLoading] = useState(true);
  const [storageSaving, setStorageSaving] = useState(false);

  useEffect(() => {
    // Motivio journey analytics: the capacity/utilization dashboard was opened —
    // record "viewed_capacity_dashboard_recurring" (server dedupes per day; the
    // habit signal is >=1 view per week).
    trackJourney("viewed_capacity_dashboard_recurring", user);

    axiosInstance
      .get("capacity/admin/instance-types")
      .then((res) => {
        const types = Array.isArray(res.data) ? res.data : [];
        setInstanceTypes(types);
        // Pre-check only the types that are currently in the allow-list.
        // When no restriction is configured the backend marks all as allowed.
        setSelected(new Set(types.filter((t) => t.allowed).map((t) => t.type)));
      })
      .catch(() => setError("Failed to load capacity data."))
      .finally(() => setLoading(false));

    axiosInstance
      .get("capacity/storage")
      .then((res) => {
        const { totalGB } = res.data;
        setStorageQuota(totalGB);
        setStorageInput(totalGB);
      })
      .catch(() => {})
      .finally(() => setStorageLoading(false));
  }, []);

  // Types the org's plan does not include are listed for visibility but can be
  // neither selected nor launched.
  const isSelectable = (t) => t.withinPlan !== false;

  // Group the catalog by family so a long list stays readable.
  const groupedTypes = useMemo(() => {
    const groups = new Map();
    instanceTypes.forEach((t) => {
      const family = t.family || "Other";
      if (!groups.has(family)) groups.set(family, []);
      groups.get(family).push(t);
    });
    return [...groups.entries()];
  }, [instanceTypes]);

  const selectableCount = instanceTypes.filter(isSelectable).length;

  const toggle = (type) => {
    const option = instanceTypes.find((t) => t.type === type);
    if (option && !isSelectable(option)) return;
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(type)) {
        next.delete(type);
      } else {
        next.add(type);
      }
      return next;
    });
  };

  const selectAll = () =>
    setSelected(new Set(instanceTypes.filter(isSelectable).map((t) => t.type)));
  const clearAll = () => setSelected(new Set());

  const save = async () => {
    setSaving(true);
    try {
      await axiosInstance.put("capacity/instance-types", {
        types: [...selected]
      });
      message.success("Allowed instance types saved.");
      // Refresh the full catalog so removed types remain visible but unchecked.
      const res = await axiosInstance.get("capacity/admin/instance-types");
      const newTypes = Array.isArray(res.data) ? res.data : [];
      setInstanceTypes(newTypes);
      setSelected(new Set(newTypes.filter((t) => t.allowed).map((t) => t.type)));
    } catch (err) {
      message.error(err?.response?.data?.error || "Failed to save.");
    } finally {
      setSaving(false);
    }
  };

  const saveStorage = async () => {
    if (!storageInput || storageInput <= 0) {
      message.error("Storage quota must be greater than 0 GiB.");
      return;
    }
    setStorageSaving(true);
    try {
      await axiosInstance.put("capacity/storage", { totalGB: storageInput });
      setStorageQuota(storageInput);
      message.success("EBS storage quota saved.");
    } catch (err) {
      message.error(err?.response?.data?.error || "Failed to save storage quota.");
    } finally {
      setStorageSaving(false);
    }
  };

  if (loading || storageLoading) {
    return (
      <div style={{ padding: 48, textAlign: "center" }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div style={{ padding: "24px", maxWidth: 1100 }}>
      <Space direction="vertical" size="large" style={{ width: "100%" }}>

        {/* ── Instance Types ─────────────────────────────────────────── */}
        <div>
          <Title level={4} style={{ marginBottom: 4 }}>
            Instance Types
          </Title>
          <Text type="secondary">
            Choose which EC2 instance types your users may launch. This
            selection applies to your organisation only. When no type is
            explicitly restricted, every catalog type is available. Uncheck a
            type and save to hide it from the launcher.
            Types marked <Tag style={{ fontSize: 10, margin: 0 }}>Untested</Tag>{" "}
            are offered but have not been verified by GoMyDesk on a golden
            image. Quota availability reflects your organisation&apos;s current
            CPU / RAM / GPU consumption.
          </Text>
        </div>

        {error && <Alert type="error" message={error} showIcon />}

        {/* Bulk actions */}
        <Space>
          <Button size="small" onClick={selectAll}>
            Select all
          </Button>
          <Button size="small" onClick={clearAll}>
            Clear all
          </Button>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {selected.size} of {selectableCount} selected
            {selectableCount !== instanceTypes.length &&
              ` (${instanceTypes.length - selectableCount} not in your plan)`}
          </Text>
        </Space>

        {/* Instance type cards, grouped by family */}
        {groupedTypes.map(([family, types]) => (
          <div key={family}>
            <Text strong style={{ fontSize: 12, textTransform: "uppercase", letterSpacing: 0.4, color: "#8c8c8c" }}>
              {family}
            </Text>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))",
                gap: 12,
                marginTop: 8
              }}
            >
              {types.map((t) => {
                const selectable = isSelectable(t);
                return (
                  <Card
                    key={t.type}
                    size="small"
                    style={{
                      border: selected.has(t.type) ? "2px solid #1677ff" : "1px solid #d9d9d9",
                      cursor: selectable ? "pointer" : "not-allowed",
                      opacity: selectable ? (t.available ? 1 : 0.7) : 0.5,
                      transition: "border 0.15s"
                    }}
                    onClick={() => toggle(t.type)}
                  >
                    <Space direction="vertical" size={4} style={{ width: "100%" }}>
                      {/* Top row: checkbox + type name + quota status */}
                      <Space style={{ justifyContent: "space-between", width: "100%" }}>
                        <Space>
                          <Checkbox
                            checked={selected.has(t.type)}
                            disabled={!selectable}
                            onChange={() => toggle(t.type)}
                          />
                          <Text strong style={{ fontSize: 13 }}>
                            {t.type}
                          </Text>
                        </Space>
                        {t.available ? (
                          <CheckCircleOutlined style={{ color: "#52c41a" }} />
                        ) : (
                          <Tooltip title={t.unavailableReason}>
                            <Space size={4}>
                              <CloseCircleOutlined style={{ color: "#ff4d4f" }} />
                              <InfoCircleOutlined style={{ color: "#aaa", fontSize: 11 }} />
                            </Space>
                          </Tooltip>
                        )}
                      </Space>

                      {/* Specs */}
                      <Space size={8} wrap>
                        <Tag style={{ fontSize: 11, margin: 0 }}>{t.vcpu} vCPU</Tag>
                        <Tag style={{ fontSize: 11, margin: 0 }}>{t.ram} GiB</Tag>
                        {t.gpu > 0 && (
                          <Tag color="purple" style={{ fontSize: 11, margin: 0 }}>
                            {t.gpu} GPU
                          </Tag>
                        )}
                        {t.validated ? (
                          <Tooltip title="GoMyDesk has booted a golden image on this type and confirmed the desktop stack comes up.">
                            <Tag color="green" style={{ fontSize: 11, margin: 0 }}>
                              Validated
                            </Tag>
                          </Tooltip>
                        ) : (
                          <Tooltip title="Offered, but not yet verified by GoMyDesk on a golden image.">
                            <Tag style={{ fontSize: 11, margin: 0 }}>Untested</Tag>
                          </Tooltip>
                        )}
                      </Space>

                      {/* Quota / plan warning */}
                      {!t.available && t.unavailableReason && (
                        <Text type="danger" style={{ fontSize: 11 }}>
                          {t.unavailableReason}
                        </Text>
                      )}
                    </Space>
                  </Card>
                );
              })}
            </div>
          </div>
        ))}

        <Button
          type="primary"
          icon={<SaveOutlined />}
          onClick={save}
          loading={saving}
          disabled={selected.size === 0}
        >
          Save allowed types
        </Button>

        <Divider />

        {/* ── EBS Storage Quota ──────────────────────────────────────── */}
        <div>
          <Title level={4} style={{ marginBottom: 4 }}>
            <DatabaseOutlined style={{ marginRight: 8 }} />
            EBS Storage Quota
          </Title>
          <Text type="secondary">
            Set the total EBS storage quota (GiB) available to the org across
            all desktops. This controls how much persistent disk space can be
            provisioned in total.
          </Text>
        </div>

        <Card size="small" style={{ maxWidth: 420 }}>
          <Space direction="vertical" size={12} style={{ width: "100%" }}>
            {storageQuota === 0 && (
              <Text type="warning" style={{ fontSize: 12 }}>
                No storage quota is configured yet. Set a value (minimum 60 GiB).
              </Text>
            )}
            <div>
              <Text strong style={{ display: "block", marginBottom: 6 }}>
                Total quota (GiB)
              </Text>
              <Space>
                <InputNumber
                  min={60}
                  max={100000}
                  step={10}
                  placeholder="e.g. 500"
                  value={storageInput || null}
                  onChange={setStorageInput}
                  style={{ width: 160 }}
                  addonAfter="GiB"
                />
                <Button
                  type="primary"
                  icon={<SaveOutlined />}
                  onClick={saveStorage}
                  loading={storageSaving}
                  disabled={storageInput === storageQuota || !storageInput || storageInput < 60}
                >
                  Save
                </Button>
              </Space>
            </div>

            {storageQuota > 0 && (
              <Text type="secondary" style={{ fontSize: 12 }}>
                Current quota: {storageQuota} GiB
              </Text>
            )}
          </Space>
        </Card>

      </Space>
    </div>
  );
}

export default CapacityManagement;
